import { createModalFocusScope } from './modal-focus-scope.js';
import { setBackgroundInert } from './modal-background-inert.js';

const allowedTriggers = new Set([
    'review_or_note_saved',
    'manual_list_created',
    'third_game_added',
    'second_session_started',
]);

const postJson = async (url, payload) => {
    if (typeof url !== 'string' || url.trim() === '') {
        return false;
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': csrfToken,
                'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            return false;
        }

        const json = await response.json().catch(() => null);

        return Boolean(json?.success);
    } catch {
        return false;
    }
};

const resolveState = () => {
    if (typeof window.__AUTH_STATE__ !== 'object' || window.__AUTH_STATE__ === null) {
        return null;
    }

    const state = window.__AUTH_STATE__.guestSoftPrompt;
    if (typeof state !== 'object' || state === null) {
        return null;
    }

    return state;
};

export const initGuestSoftPrompt = () => {
    const state = resolveState();
    if (state === null || state.enabled !== true) {
        return;
    }

    const trigger = String(state.trigger ?? '').trim();
    if (trigger === '' || !allowedTriggers.has(trigger)) {
        return;
    }

    const modal = document.querySelector('[data-guest-soft-prompt]');
    const dialog = modal instanceof HTMLElement
        ? modal.querySelector('[data-guest-soft-prompt-dialog]')
        : null;
    const registerButton = modal instanceof HTMLElement
        ? modal.querySelector('[data-guest-soft-prompt-register]')
        : null;

    if (!(modal instanceof HTMLElement) || !(dialog instanceof HTMLElement)) {
        return;
    }

    if (registerButton instanceof HTMLAnchorElement && typeof state.registerUrl === 'string' && state.registerUrl !== '') {
        registerButton.href = state.registerUrl;
    }

    let isClosing = false;

    const closeModal = async () => {
        if (isClosing || modal.hidden) {
            return;
        }

        isClosing = true;
        modal.hidden = true;
        setBackgroundInert(modal, false);
        focusScope.deactivate();

        await postJson(String(state.dismissUrl ?? ''), { trigger });
    };
    const focusScope = createModalFocusScope({
        dialog,
        requestClose: () => {
            void closeModal();
        },
    });
    if (focusScope === null) {
        return;
    }

    const openModal = async () => {
        const shown = await postJson(String(state.shownUrl ?? ''), { trigger });
        if (!shown) {
            return;
        }

        const triggerElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        modal.hidden = false;
        focusScope.activate(triggerElement);
        focusScope.focus(dialog);
        setBackgroundInert(modal, true);
    };

    modal.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) {
            return;
        }

        if (target.closest('[data-guest-soft-prompt-close]')) {
            void closeModal();
        }
    });
    window.setTimeout(() => {
        void openModal();
    }, 220);
};
